import {
  compose, reduce, mergeRight, map, toPairs,
  mergeDeepRight,
} from 'ramda';
import { useMemo } from 'react';
import { useSelector } from 'react-redux';

import { AmountType } from '@ezugi/constants';
import { formatAmount } from '@ezugi/utils';

import { FormatConfig } from '../store/reducers/config';
import { formatSelector, userCurrencySelector } from '../store/selectors';

type FormatAmountOptions = {
  value: string | number;
  type: AmountType;
  showCurrencySymbol?: boolean;
  showCurrencyCode?: boolean;
};

const getDisplayValues = <T>(
  format: FormatConfig | undefined,
  currency: string,
  values: AmountValues<T>
) => compose(
    reduce(mergeRight, {}),
    map(
      ([ key, { value, type, showCurrencySymbol, showCurrencyCode } ]: [
        string,
        FormatAmountOptions
      ]) => ({
        [key]: formatAmount(
          value,
          type,
          format,
          currency,
          showCurrencySymbol,
          showCurrencyCode
        ),
      })
    ),
    toPairs
  )(values);

type DisplayValue<T = number> = {
  value: T;
  type: AmountType;
  showCurrencySymbol?: boolean;
  showCurrencyCode?: boolean;
};

type AmountValues<T> = {
  [key: string]: DisplayValue<T>;
};

type UseAmountReturn<T extends AmountValues<any>> = {
  [K in keyof T]: string;
};

export const useAmount = <T extends AmountValues<any>>({
  values,
  format,
}: {
  values: T;
  format?: Partial<FormatConfig>
}): UseAmountReturn<T> => {
  const formatFromBO = useSelector(formatSelector);
  const currency = useSelector(userCurrencySelector);

  const formatConfig = format ? mergeDeepRight(formatFromBO ?? {}, format) as (FormatConfig | undefined) : formatFromBO;

  const displayValues = useMemo(
    () =>
      // eslint-disable-next-line implicit-arrow-linebreak
      getDisplayValues(formatConfig, currency, values),
    [ values, formatConfig, currency ]
  ) as UseAmountReturn<T>;

  return displayValues;
};
